import AdvancedSearch from '@ashn/network-search-react/AdvancedSearch';
import AdvancedSearchSkeleton from '@ashn/network-search-react/AdvancedSearchSkeleton';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { useQuery } from 'react-query';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { GetNetworkSearchContext } from '../../data/apiCalls';
import { NETWORK_SEARCH_ROUTES } from '../../scripts/routes';
import {
	CA_TIMELY_ACCESS_CONTENT,
	DIRECTORY_ACCURACY_CONTENT,
	getAttributionNotice,
	getDisclaimerContent,
	getHeaderText,
	getLanguageAccessContent,
	getPlanEligibilityNotice,
	getPlanNavigationNotice,
	getPriorityHealthEligibilityNotice,
	getPriorityHealthMemberActions,
	LANGUAGE_ACCESS_CONTENT,
	showDirectoryAccuracyBlock,
} from './logic';
import {
	CaTimelyAccess,
	DirectoryAccuracyBlock,
	Disclaimer,
	InvalidPlanMessage,
	LanguageAccess,
	PlanEligibilityNotice,
	PlanNavigationNotice,
	PriorityHealthMemberActions,
} from './SearchSections';

const Search = () => {
	const { healthplanCode } = useParams();
	const healthplanCodeLowercase = healthplanCode?.toLowerCase();
	const navigate = useNavigate();
	const clientDetails = useQuery(
		['networkSearchContext', healthplanCode],
		() => GetNetworkSearchContext(healthplanCode),
		{
			enabled: !!healthplanCode,
			staleTime: Infinity,
			refetchOnWindowFocus: false,
			retry: false,
		}
	);

	// The BFF returns 404 for an unknown plan code, which puts the query in error.
	const isValidCode = clientDetails.isSuccess && !!clientDetails.data?.data;
	const contracts = clientDetails.data?.data?.contracts || [];

	const planEligibilityNotice = getPlanEligibilityNotice(
		healthplanCodeLowercase
	);
	const planNavigationNotice = getPlanNavigationNotice(
		healthplanCodeLowercase
	);
	const disclaimer = getDisclaimerContent(healthplanCodeLowercase);
	const attributionNotice = getAttributionNotice(healthplanCodeLowercase);
	const showDirectoryBlock = showDirectoryAccuracyBlock(
		healthplanCodeLowercase
	);
	const languageAccessResult = getLanguageAccessContent(
		healthplanCodeLowercase
	);
	const languageAccessContent = languageAccessResult
		? LANGUAGE_ACCESS_CONTENT[languageAccessResult.type]
		: null;

	return (
		<>
			{clientDetails.isLoading ? (
				<>
					<Skeleton
						variant='rounded'
						height={40}
						width={320}
						sx={{ mb: 3 }}
						data-testid='skeletonLoading'
					/>
					<Box sx={{ maxWidth: 800 }}>
						<AdvancedSearchSkeleton />
					</Box>
				</>
			) : (
				<>
					<Typography variant='h4' sx={{ mb: 3 }}>
						{getHeaderText(healthplanCodeLowercase)}
					</Typography>

					{!isValidCode ? (
						<InvalidPlanMessage />
					) : (
						<>
							{planEligibilityNotice && (
								<PlanEligibilityNotice
									content={planEligibilityNotice}
								/>
							)}
							{planNavigationNotice && (
								<PlanNavigationNotice
									content={planNavigationNotice}
								/>
							)}

							{healthplanCodeLowercase !== 'ihp' && (
								<Box sx={{ maxWidth: 800, mb: 32 }}>
									<AdvancedSearch
										contracts={contracts}
										onSearch={searchParameters => {
											const params = {
												specialtyCode: String(
													searchParameters?.specialtyCode ??
														''
												),
												address:
													searchParameters?.address ??
													'',
											};

											// Optional advanced search fields are only
											// added to the URL when provided, keeping
											// it free of empty query params.
											if (searchParameters?.fullName) {
												params.fullName =
													searchParameters.fullName;
											}
											if (searchParameters?.clinicName) {
												params.clinicName =
													searchParameters.clinicName;
											}
											if (searchParameters?.npi) {
												params.npi =
													searchParameters.npi;
											}
											if (
												searchParameters?.licenseNumber
											) {
												params.licenseNumber =
													searchParameters.licenseNumber;
											}

											navigate({
												pathname:
													NETWORK_SEARCH_ROUTES.results,
												search: createSearchParams(
													params
												).toString(),
											});
										}}
									/>
								</Box>
							)}

							{disclaimer && <Disclaimer content={disclaimer} />}
							{disclaimer?.hasEmbeddedLanguageAccess && (
								<LanguageAccess
									type='standard'
									content={LANGUAGE_ACCESS_CONTENT.standard}
								/>
							)}

							{attributionNotice && (
								<Typography variant='body2' gutterBottom mb={2}>
									{attributionNotice}
								</Typography>
							)}

							{showDirectoryBlock && (
								<DirectoryAccuracyBlock
									content={DIRECTORY_ACCURACY_CONTENT}
								>
									{languageAccessContent && (
										<LanguageAccess
											type={languageAccessResult.type}
											content={languageAccessContent}
										/>
									)}
								</DirectoryAccuracyBlock>
							)}
							{getPriorityHealthMemberActions(
								healthplanCodeLowercase
							) && (
								<PriorityHealthMemberActions
									content={getPriorityHealthMemberActions(
										healthplanCodeLowercase
									)}
								/>
							)}

							{getPriorityHealthEligibilityNotice(
								healthplanCodeLowercase
							) && (
								<Typography variant='body2' gutterBottom mb={2}>
									{getPriorityHealthEligibilityNotice(
										healthplanCodeLowercase
									)}
								</Typography>
							)}

							<CaTimelyAccess
								content={CA_TIMELY_ACCESS_CONTENT}
							/>
						</>
					)}
				</>
			)}
		</>
	);
};

export default Search;
